import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { dashboardApi } from "../api/dashboard";
import type { DashboardSummary } from "../api/types";
import { Shell } from "../app/Shell";
import { FAMILY_LABELS, PRODUCT_FAMILIES } from "../catalog/taxonomy";
import { ErrorNotice, formatDateTime } from "../components/ui";
import { ORDER_STATUS_LABELS } from "../orders/orderStatus";
import "./dashboard.css";

/**
 * The first screen after signing in.
 *
 * Two things: the actions an administrator comes here to start, and a concise
 * account of what the shop currently looks like.
 *
 * THE SUMMARY IS READ, NEVER COMPUTED
 * Every number below is an integer the backend counted, and this file contains
 * no rule about any of them. Which order states are still open comes from the
 * order lifecycle graph; what a stock quantity MEANS comes from each item's own
 * configured threshold; which family a product belongs to comes from the one
 * category resolver. A second definition of any of those in a browser is how
 * two screens come to disagree about one shop.
 *
 * WHAT WAS HERE BEFORE
 * A table of the first hundred products, grouped and counted in the browser.
 * It over-fetched a hundred full rows to print three numbers, those numbers
 * were silently wrong past a hundred products, families with nothing in them
 * disappeared, and the table was a worse copy of Product Management — which
 * remains the one place products are listed.
 *
 * WHAT THIS SCREEN DOES NOT CLAIM
 * "Open" means an order still has somewhere to go, and nothing more. The domain
 * defines no urgency, so nothing here is called urgent, overdue or late, and no
 * number is styled as an alarm.
 */

interface ActionCard {
  to: string;
  title: string;
  blurb: string;
  tone: "add" | "edit";
}

/**
 * Two destinations, because there are only two.
 *
 * Adding is its own act — there is no product to choose first. Everything else
 * an administrator does (editing, resuming an interrupted import, publishing
 * changes, archiving) is done *to* a product, so it starts by finding that
 * product in Products.
 */
const ACTIONS: ActionCard[] = [
  {
    to: "/products/add",
    title: "Add Product",
    blurb: "Create a new product from one product folder.",
    tone: "add",
  },
  {
    to: "/products",
    title: "Products",
    blurb: "Find a product to edit, resume, publish or archive.",
    tone: "edit",
  },
];

/** The order states the summary reports, in lifecycle order.
 *
 *  The BACKEND decides which states are open — they are the ones with an
 *  outgoing transition — and sends exactly these three. This list only fixes
 *  the order they are drawn in, and the words come from the Orders section's
 *  own vocabulary so the two screens cannot describe one state differently. */
const OPEN_ORDER_STATES = ["placed", "confirmed", "fulfilment_pending"] as const;

/**
 * The catalogue rows, labelled as the destination labels them.
 *
 * Deliberately the wording of Product Management's own status filter rather
 * than the badge vocabulary used inside a product row: following one of these
 * links puts that filter into that state, so the label an administrator reads
 * here is the one they are about to see selected there.
 */
const PRODUCT_STATES = [
  { key: "draft", label: "Draft" },
  { key: "published", label: "Published" },
  { key: "unpublished", label: "Unpublished" },
  { key: "archived", label: "Archived" },
] as const;

/** One counted thing, and the place that produced it.
 *
 *  Always a link: a number an administrator cannot act on is trivia, and the
 *  destination carries the same filter the number was counted under. */
function SummaryLine({
  to,
  label,
  count,
}: {
  to: string;
  label: string;
  count: number;
}) {
  return (
    <Link className="summaryLine" to={to}>
      <span className="summaryLine__label">{label}</span>
      <span className="summaryLine__count num">{count}</span>
    </Link>
  );
}

function OperationalOverview({ summary }: { summary: DashboardSummary }) {
  const { orders, inventory, products, families } = summary;

  return (
    <div className="summaryGrid">
      <section className="panel summaryPanel" aria-label="Orders">
        <header className="panel__head">
          <h3 className="panel__title">Orders</h3>
        </header>
        <div className="summaryPanel__body">
          {OPEN_ORDER_STATES.map((state) => (
            <SummaryLine
              key={state}
              to={`/orders?status=${state}`}
              label={ORDER_STATUS_LABELS[state]}
              count={orders[state]}
            />
          ))}
          {/* Structurally non-terminal, which is what "open" means here.

              DELIBERATELY NOT A LINK. It is the sum of the three states above,
              and the Orders screen filters on ONE status — there is no address
              that means "these three at once", and `status=open` is not a
              state this shop has. Pointing it at the unfiltered list would put
              a number beside a destination that answers a different question,
              which is the one thing a summary must never do. Orders itself is
              reached from the sidebar, as every section here is. */}
          <div className="summaryLine summaryLine--total">
            <span className="summaryLine__label">Open orders</span>
            <span className="summaryLine__count num">{orders.open_total}</span>
          </div>
        </div>
      </section>

      <section className="panel summaryPanel" aria-label="Stock">
        <header className="panel__head">
          <h3 className="panel__title">Stock</h3>
        </header>
        <div className="summaryPanel__body">
          {/* Both links carry the lifecycle the counts were taken under, so the
              page they open is scoped to the same stock they describe. */}
          <SummaryLine
            to={`/inventory?lifecycle=${inventory.lifecycle}&availability=low_stock`}
            label="Low stock"
            count={inventory.low_stock}
          />
          <SummaryLine
            to={`/inventory?lifecycle=${inventory.lifecycle}&availability=out_of_stock`}
            label="Out of stock"
            count={inventory.out_of_stock}
          />
          <p className="summaryPanel__scope muted">
            Published products only. Low stock uses each item&rsquo;s own
            configured threshold.
          </p>
        </div>
      </section>

      <section className="panel summaryPanel" aria-label="Catalogue">
        <header className="panel__head">
          <h3 className="panel__title">Catalogue</h3>
        </header>
        <div className="summaryPanel__body">
          {PRODUCT_STATES.map((state) => (
            <SummaryLine
              key={state.key}
              to={`/products?status=${state.key}`}
              label={state.label}
              count={products[state.key]}
            />
          ))}
        </div>
      </section>

      <section className="panel summaryPanel" aria-label="Product families">
        <header className="panel__head">
          <h3 className="panel__title">Product families</h3>
        </header>
        <div className="summaryPanel__body">
          {/* All three, always, including at zero: a family that vanished when
              it emptied would leave no way to reach the section somebody is
              trying to fill. */}
          {PRODUCT_FAMILIES.map((family) => (
            <SummaryLine
              key={family}
              to={`/products?family=${family}`}
              label={FAMILY_LABELS[family]}
              count={families[family]}
            />
          ))}
          {/* Not a link and not a family: there is no screen that lists these,
              and filing them under one would be inventing a classification the
              product does not have. The sentence states the consequence the
              publish gate already enforces. */}
          {families.unclassified > 0 ? (
            <p className="summaryPanel__scope muted">
              <span className="num">{families.unclassified}</span> product
              {families.unclassified === 1 ? "" : "s"} not yet classified.
              A product cannot be published until it has a category.
            </p>
          ) : null}
        </div>
      </section>
    </div>
  );
}

export function DashboardHome() {
  const summary = useQuery({
    queryKey: ["dashboard", "summary"],
    queryFn: ({ signal }) => dashboardApi.summary(signal),
  });

  return (
    <Shell title="Dashboard">
      {/* The actions come first in the markup as well as on screen, so they are
          also the first thing a keyboard or screen reader reaches. */}
      <nav className="actions" aria-label="Product actions">
        {ACTIONS.map((action) => (
          <Link
            key={action.to}
            to={action.to}
            className={`actionCard actionCard--${action.tone}`}
          >
            <span className="actionCard__title">{action.title}</span>
            <span className="actionCard__blurb">{action.blurb}</span>
          </Link>
        ))}
      </nav>

      <section className="stack" aria-label="Operational overview">
        <div className="summaryHead">
          <h2 className="summaryHead__title">Operational overview</h2>
          {summary.isSuccess ? (
            <span className="muted summaryHead__stamp">
              as at {formatDateTime(summary.data.generated_at)}
            </span>
          ) : null}
        </div>

        {/* Retry sits beside the message rather than inside it, so the alert an
            assistive reader announces is the sentence and not the button.

            A failure renders NO counts. Zeros in place of an answer look
            exactly like a quiet shop and mean the opposite thing. */}
        {summary.isError ? (
          <div className="summaryFailure">
            <ErrorNotice error={summary.error} />
            <button
              type="button"
              className="btn"
              disabled={summary.isFetching}
              onClick={() => void summary.refetch()}
            >
              {summary.isFetching ? "Retrying…" : "Retry"}
            </button>
          </div>
        ) : null}

        {summary.isPending ? (
          <div className="empty" role="status">
            Loading the overview…
          </div>
        ) : null}

        {summary.isSuccess ? (
          <OperationalOverview summary={summary.data} />
        ) : null}
      </section>
    </Shell>
  );
}
